import { Modal, Checkbox, Space, Typography, Button, Tooltip } from "antd";
import React, { useContext, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateApply1DsKeywordMappingModalDisplay } from "@features/modalSlice";
import { map } from "underscore";
import { IMainContext } from "@/ts/common-types";
import { applyKeyword1DSMapping, internationalizeNumericValue } from "@/ts/utils";
import { InfoCircleOutlined } from "@ant-design/icons";
import { mainContext } from "@/ts/constants";
import { showToast } from "@/ts/modals";

const { Text, Title } = Typography;

type IApply1DSKeywordMappingModalProps = {
    show: boolean;
};

const Apply1DSKeywordMappingModal: React.FC<IApply1DSKeywordMappingModalProps> = ({
    show,
}) => {
    const { keywordMappingGridApi } = useContext<IMainContext>(mainContext);
    const dispatch = useDispatch();
    const [selectedParams, setSelectedParams] = useState<string[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const keywordMappingSelectionData = useSelector(
        (state: { keywordMappingSelectionData: { value: any[] } }) =>
            state.keywordMappingSelectionData.value
    );

    const bulkKeywordMappingCount = useSelector(
        (state: { bulkKeywordMappingCount: { value: number } }) =>
            state.bulkKeywordMappingCount.value
    );

    const keywordMappingFilterModel = useSelector(
        (state: { keywordMappingFilterModel: { value: any } }) =>
            state.keywordMappingFilterModel.value
    );

    const keywordMappingAllPagesSelected = useSelector(
        (state: { keywordMappingAllPagesSelected: { value: boolean } }) =>
            state.keywordMappingAllPagesSelected.value
    );

    const mappingParameters = [
        { id: 'category_1ds', label: "1DS Category" },
        { id: 'subcategory_1ds', label: "1DS Subcategory" },
        { id: 'brand_1ds', label: "1DS Brand" }
    ];

    const handleParameterChange = (checkedValues: string[]) => {
        setSelectedParams(checkedValues);
    };

    const handleSubmit = async () => {
        try {
            setIsSubmitting(true);

            const applyMappingConfig = { ...(keywordMappingSelectionData.length && !keywordMappingAllPagesSelected
                 ? { keywords: keywordMappingSelectionData .map(d => d.sys_keyword_id) .filter(id => id != null), } 
                 : { filterModel: keywordMappingFilterModel, }), update_params
                 : selectedParams, };

            const response = await applyKeyword1DSMapping(applyMappingConfig);

            if (!response.status) {
                throw new Error(response.message || 'Failed to apply 1DS mapping');
            }

            showToast("success", response.message || 'Successfully applied 1DS mapping');

            if (keywordMappingGridApi) {
                keywordMappingGridApi.refreshServerSideStore({
                    route: [],
                    purge: true,
                });
            }

            dispatch(updateApply1DsKeywordMappingModalDisplay(false));
        } catch (error) {
            console.error("Error applying 1DS mapping:", error);
            showToast(
                "danger",
                error instanceof Error
                    ? error.message
                    : "An error occurred while applying the mapping."
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        dispatch(updateApply1DsKeywordMappingModalDisplay(false));
    };

    return (
        <Modal
            centered
            title={
                <Space
                    direction="vertical"
                    size={8}
                    style={{ width: "100%", paddingRight: "24px" }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                        }}
                    >
                        <Title level={4} style={{ margin: 0 }}>
                            Apply 1DS Mapping
                        </Title>
                        <Tooltip title="The mappings will only be applied to those keywords that don't have any value mapped already in the corresponding parameter's column.">
                            <InfoCircleOutlined
                                style={{ color: "#8c8c8c", fontSize: "18px" }}
                            />
                        </Tooltip>
                    </div>
                    <div
                        className="d-flex align-items-center"
                        style={{
                            fontSize: "0.8rem",
                            color: "#666",
                            fontWeight: "400",
                            backgroundColor: "#f5f5f5",
                            padding: "4px 12px",
                            borderRadius: "4px",
                            width: "fit-content",
                            marginTop: "4px",
                        }}
                    >
                        Selected Keywords:{" "}
                        <span style={{ marginLeft: "6px", fontWeight: "600" }}>
                            {internationalizeNumericValue(
                                keywordMappingAllPagesSelected
                                    ? bulkKeywordMappingCount
                                    : keywordMappingSelectionData.length || bulkKeywordMappingCount
                            )}
                        </span>
                    </div>
                </Space>
            }
            open={show}
            onCancel={handleCancel}
            width={400}
            styles={{
                body: {
                    padding: "24px",
                },
            }}
            footer={[
                <Button
                    key="cancel"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                >
                    Cancel
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    onClick={handleSubmit}
                    loading={isSubmitting}
                    disabled={selectedParams.length === 0 || isSubmitting}
                >
                    Submit
                </Button>,
            ]}
        >
            <Checkbox.Group
                onChange={handleParameterChange}
                value={selectedParams}
                style={{ width: "100%" }}
            >
                <Space
                    direction="vertical"
                    style={{ width: "100%" }}
                    size="middle"
                >
                    {map(mappingParameters, (param) => (
                        <div
                            key={param.id}
                            style={{
                                padding: "12px 16px",
                                borderRadius: "6px",
                                backgroundColor: selectedParams.includes(param.id)
                                    ? "#e6f7ff"
                                    : "#fafafa",
                                border: selectedParams.includes(param.id)
                                    ? "1px solid #1890ff"
                                    : "1px solid #f0f0f0",
                                cursor: "pointer",
                                transition: "all 0.3s ease",
                                boxShadow: selectedParams.includes(param.id)
                                    ? "0 2px 8px rgba(24, 144, 255, 0.15)"
                                    : "0 1px 2px rgba(0, 0, 0, 0.05)",
                                display: "flex",
                                alignItems: "center",
                                maxWidth: "300px",
                                margin: "0 auto",
                            }}
                            onMouseEnter={(e) => {
                                if (!selectedParams.includes(param.id)) {
                                    e.currentTarget.style.borderColor = "#1890ff";
                                    e.currentTarget.style.boxShadow =
                                        "0 2px 8px rgba(24, 144, 255, 0.15)";
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!selectedParams.includes(param.id)) {
                                    e.currentTarget.style.borderColor = "#f0f0f0";
                                    e.currentTarget.style.boxShadow =
                                        "0 1px 2px rgba(0, 0, 0, 0.05)";
                                }
                            }}
                            onClick={() =>
                                setSelectedParams(
                                    selectedParams.includes(param.id)
                                        ? selectedParams.filter(
                                            (id) => id !== param.id
                                        )
                                        : [...selectedParams, param.id]
                                )
                            }
                        >
                            <Checkbox value={param.id}>
                                <Text style={{ fontSize: "14px" }}>
                                    {param.label}
                                </Text>
                            </Checkbox>
                        </div>
                    ))}
                </Space>
            </Checkbox.Group>
        </Modal>
    );
};

export default Apply1DSKeywordMappingModal;